<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Eye } from 'lucide-vue-next'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDate } from '@yuksalish/ui'
import { useShopfloorApi } from '../composables/useShopfloorApi'
import type { ShopfloorOrder } from '../types/shopfloor'

const { t } = useI18n()
const { listActiveOrders } = useShopfloorApi()

const rows = ref<ShopfloorOrder[]>([])
const meta = reactive({ page: 1, per_page: 20, total: 0, total_pages: 1 })
const search = ref('')
const statusFilter = ref('')
const loading = ref(true)
const error = ref('')
let timer: ReturnType<typeof setTimeout>

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listActiveOrders({
      search: search.value || undefined,
      status: statusFilter.value || undefined,
      page: meta.page,
      per_page: meta.per_page,
    })
    meta.page = result.page
    meta.per_page = result.per_page
    meta.total = result.total
    meta.total_pages = result.total_pages
    rows.value = result.items
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

watch([search, statusFilter], () => {
  clearTimeout(timer)
  meta.page = 1
  timer = setTimeout(load, 300)
})

watch(() => meta.page, load)

onMounted(load)

const pageRange = computed(() => {
  const first = meta.total === 0 ? 0 : (meta.page - 1) * meta.per_page + 1
  const last = Math.min(meta.page * meta.per_page, meta.total)
  return { first, last }
})

function statusClass(status: string) {
  return {
    'in_progress': 'status-progress',
    'completed': 'status-completed',
    'planned': 'status-pending',
  }[status] ?? ''
}

function orderStatusLabel(status: string): string {
  const key = `shopfloor.orders.statuses.${status}`
  const value = t(key)
  return value === key ? status : value
}

function progressPercent(order: ShopfloorOrder): number {
  if (!order.planned_quantity) return 0
  return Math.round((Number(order.produced_quantity || 0) / order.planned_quantity) * 100)
}

function goToPage(page: number) {
  if (page < 1 || page > meta.total_pages || page === meta.page) return
  meta.page = page
}
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('shopfloor.orders.eyebrow') }}</p>
      <h2>{{ t('shopfloor.orders.title') }}</h2>
      <p>{{ t('shopfloor.orders.description') }}</p>
    </div>
  </div>

  <!-- Filters -->
  <div class="filter-bar">
    <input
      v-model="search"
      type="search"
      :placeholder="t('shopfloor.orders.searchPlaceholder')"
      class="search-input"
    />
    <select v-model="statusFilter" class="filter-select">
      <option value="">{{ t('shopfloor.orders.allStatuses') }}</option>
      <option value="in_progress">{{ t('shopfloor.orders.inProgress') }}</option>
      <option value="planned">{{ t('shopfloor.orders.pending') }}</option>
      <option value="completed">{{ t('shopfloor.orders.completed') }}</option>
    </select>
  </div>

  <!-- Loading -->
  <div v-if="loading" class="state-message">
    <p>{{ t('shopfloor.orders.loading') }}</p>
  </div>

  <!-- Error -->
  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('shopfloor.state.retry') }}</button>
  </div>

  <!-- Empty -->
  <div v-else-if="rows.length === 0" class="state-message">
    <p>{{ t('shopfloor.orders.empty') }}</p>
  </div>

  <!-- Table -->
  <template v-else>
    <div class="table-scroll">
    <table class="data-table">
      <thead>
        <tr>
          <th>{{ t('shopfloor.orders.code') }}</th>
          <th>{{ t('shopfloor.orders.product') }}</th>
          <th>{{ t('shopfloor.orders.department') }}</th>
          <th>{{ t('shopfloor.orders.plannedQty') }}</th>
          <th>{{ t('shopfloor.orders.producedQty') }}</th>
          <th>{{ t('shopfloor.orders.progress') }}</th>
          <th>{{ t('shopfloor.orders.status') }}</th>
          <th>{{ t('shopfloor.orders.actions') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="order in rows" :key="order.id">
          <td class="cell-code">
            <RouterLink
              :to="`/orders/${order.id}`"
              class="order-code-link"
              :title="t('shopfloor.orders.viewLots')"
            >
              {{ order.mo_code }}
            </RouterLink>
          </td>
          <td>{{ order.product_name ?? '—' }}</td>
          <td>{{ order.department_name ?? '—' }}</td>
          <td>{{ order.planned_quantity.toLocaleString() }}</td>
          <td>{{ Number(order.produced_quantity || 0).toLocaleString() }}</td>
          <td>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progressPercent(order) + '%' }" />
            </div>
            <span class="progress-text">{{ progressPercent(order) }}%</span>
          </td>
          <td>
            <span :class="['status-badge', statusClass(order.status)]">
              {{ orderStatusLabel(order.status) }}
            </span>
          </td>
          <td>
            <RouterLink
              :to="`/orders/${order.id}`"
              class="link-action icon-action"
              :title="t('shopfloor.orders.viewLots')"
              :aria-label="t('shopfloor.orders.viewLots')"
            >
              <Eye :size="18" aria-hidden="true" />
            </RouterLink>
          </td>
        </tr>
      </tbody>
    </table>
    </div>

    <div class="pagination">
      <span class="page-info">{{ pageRange.first }}–{{ pageRange.last }} {{ t('shopfloor.pagination.of') }} {{ meta.total }}</span>
      <div class="page-buttons">
        <button :disabled="meta.page <= 1" @click="goToPage(meta.page - 1)">{{ t('shopfloor.pagination.previous') }}</button>
        <button :disabled="meta.page >= meta.total_pages" @click="goToPage(meta.page + 1)">{{ t('shopfloor.pagination.next') }}</button>
      </div>
    </div>
  </template>
</template>

<style scoped>
.page-heading {
  margin-bottom: 20px;
}
.eyebrow {
  margin: 0 0 4px;
  color: #65736e;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
h2 { margin: 0 0 4px; font-size: 22px; font-weight: 700; }
.page-heading p { margin: 0; color: #5f6b6a; font-size: 14px; }

.filter-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}
.search-input {
  flex: 1;
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
}
.filter-select {
  padding: 8px 14px;
  border: 1px solid #d4d8d5;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  min-width: 160px;
}

.state-message {
  padding: 40px 0;
  text-align: center;
  color: #5f6b6a;
}
.state-message.error { color: #b33a3a; }

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid #e2e6e3;
  border-radius: 10px;
  overflow: hidden;
}
.data-table th {
  text-align: left;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #65736e;
  background: #f6f8f7;
  border-bottom: 1px solid #e2e6e3;
}
.data-table td {
  padding: 12px 16px;
  font-size: 13px;
  color: #1a2b26;
  border-bottom: 1px solid #f0f2f1;
}
.data-table tr:last-child td { border-bottom: none; }
.cell-code { font-weight: 700; font-family: monospace; }
.order-code-link {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 2px;
  color: #27705d;
  font-weight: 700;
  text-decoration: none;
}
.order-code-link:hover { text-decoration: underline; }

.progress-bar {
  display: inline-block;
  width: 80px;
  height: 6px;
  background: #e2e6e3;
  border-radius: 3px;
  vertical-align: middle;
  margin-right: 8px;
}
.progress-fill {
  height: 100%;
  background: #27705d;
  border-radius: 3px;
  transition: width 0.3s;
}
.progress-text { font-size: 12px; color: #65736e; }

.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  background: #eaf0ed;
  color: #3d5a4f;
}
.status-progress { background: #dcefe7; color: #1f5c4b; }
.status-completed { background: #e2e6e3; color: #4a5d56; }
.status-pending { background: #fef3cd; color: #856404; }

.link-action {
  color: #27705d;
  font-weight: 600;
  font-size: 12px;
  text-decoration: none;
}
.link-action:hover { text-decoration: underline; }

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
}
.page-info {
  font-size: 13px;
  color: #65736e;
}
.page-buttons {
  display: flex;
  gap: 8px;
}
.page-buttons button {
  padding: 6px 16px;
  background: #fff;
  color: #27705d;
  border: 1px solid #d4d8d5;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.page-buttons button:disabled {
  opacity: 0.4;
  cursor: default;
}
.page-buttons button:not(:disabled):hover { background: #f0f5f3; }

.btn-primary {
  display: inline-block;
  margin-top: 12px;
  padding: 8px 20px;
  background: #27705d;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.btn-primary:hover { background: #1f5c4b; }
</style>
